import { Css } from "~generated/css";
import { currencyFormatter } from "~utils";
import {
  SummaryPrintConfiguration_CheckoutConfigFragment,
  SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment,
} from "../SummaryPrint.page.generated";
import { PDFPage } from "./PDFPage";
import { PrintHeader } from "./PrintHeader";

type EstimatedInvoiceScheduleReportProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  shouldDisplayPricing: boolean;
  estimatedInvoiceSchedule?: SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment[];
  invoiceCategoryNames?: Record<string, string>;
};

export function EstimatedInvoiceScheduleReport({
  data,
  estimatedInvoiceSchedule,
  invoiceCategoryNames = {},
}: EstimatedInvoiceScheduleReportProps) {
  const { name, status, createdAt, reservedAt, expiresAt, customerEmail } = data;

  if (!estimatedInvoiceSchedule || estimatedInvoiceSchedule.length === 0) return null;

  const totalInCents = estimatedInvoiceSchedule.reduce((sum, item) => sum + item.invoiceAmountInCents, 0);

  return (
    <PDFPage>
      <div css={Css.relative.h100.w100.bgWhite.$}>
        <div css={Css.df.ma.fdc.gap2.p1.pb4.asc.maxContainer.$}>
          <PrintHeader
            status={status}
            expiredAt={expiresAt}
            createdAt={createdAt}
            reservedAt={reservedAt}
            configurationName={name}
            customerEmail={customerEmail}
          />
          <h2 css={Css.t18.fw500.mb1.$}>Estimated Invoice Schedule</h2>
          {/* Table header */}
          <div css={Css.df.w100.jcsb.pb1.add("borderBottom", "2px solid").add("borderBottomColor", "#374151").$}>
            <span css={Css.t14.fw500.gray800.$}>Category</span>
            <span css={Css.t14.fw500.gray800.$}>Amount</span>
          </div>
          {/* Table rows */}
          {estimatedInvoiceSchedule.map((item) => (
            <div
              key={item.costCodeInvoiceCategory}
              css={Css.df.w100.jcsb.pyPx(8).add("borderBottom", "1px dotted").add("borderBottomColor", "#d1d5db").$}
            >
              <span css={Css.t14.gray700.$}>
                {invoiceCategoryNames[item.costCodeInvoiceCategory] ?? item.costCodeInvoiceCategory}
              </span>
              <span css={Css.t14.gray700.$}>{currencyFormatter(item.invoiceAmountInCents)}</span>
            </div>
          ))}
          {/* Total row */}
          <div css={Css.df.w100.jcsb.ptPx(12).$}>
            <span css={Css.t14.fw500.gray800.$}>Total</span>
            <span css={Css.t14.fw500.gray800.$}>{currencyFormatter(totalInCents)}</span>
          </div>
          {/* Disclaimer */}
          <div css={Css.t12.pt2.tac.gray700.$}>
            <sup>*</sup>Estimated invoice schedule is subject to change. Final amounts will be confirmed prior to
            contract execution.
          </div>
        </div>
      </div>
    </PDFPage>
  );
}
